import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { updateNavigateToSpesificMessage } from "../../../../store/Slices/NavigateToSpesificMessageSlice";
import { Message } from "../../../../types/MessageListType";
import { useTranslateText } from "../../../../hooks/useTranslateText";
import ChatListMediaThumb from "../../../../components/ChatListMediaThumb";
import { toSameOriginMediaUrl } from "../../../../utils/mediaPlayUrl";
import {
  looksLikeMediaPathOrUrl,
  resolveMediaCaption,
} from "../../../../utils/mediaMessageUrl";

const PREVIEW_TYPES = new Set(["image", "gif", "video", "circle"]);

function replyTypeLabelKey(type: string): string | null {
  switch (type) {
    case "image":
      return "Photo";
    case "gif":
      return "GIF";
    case "video":
      return "Video";
    case "circle":
      return "Video message";
    case "doc":
      return "Document";
    case "location":
      return "Location";
    case "audio":
      return "Audio";
    case "voice":
      return "Voice message";
    case "contact":
      return "Contact";
    default:
      return null;
  }
}

function replyThumbUrl(message: Message): string {
  const type = String(message.message_type || "");
  const thumb =
    String(message.message_thumbnail || "").trim() ||
    String(message.message_preview || "").trim();
  if (thumb) return toSameOriginMediaUrl(thumb);
  if (type === "image" || type === "gif") {
    return toSameOriginMediaUrl(String(message.media_url || "").trim());
  }
  return "";
}

export default function ShowReplyMessage({
  reply_to,
  messageData,
  variant = "bubble",
}: {
  reply_to: number;
  messageData: Message;
  variant?: "bubble" | "compose";
}) {
  const userData = useAppSelector((state) => state.userData);
  const dispatch = useAppDispatch();
  const translate = useTranslateText();
  const repliedMessage = messageData;
  const isCompose = variant === "compose";

  if (!repliedMessage) return null;

  const type = String(repliedMessage.message_type || "");
  const isOwnReply =
    Number(repliedMessage?.User?.user_id) === Number(userData.user_id) ||
    Number(repliedMessage?.sender_id) === Number(userData.user_id);
  const author = isOwnReply
    ? translate("You")
    : repliedMessage?.User?.full_name || "";
  const caption = resolveMediaCaption(repliedMessage);
  const typeKey = replyTypeLabelKey(type);
  const typeLabel = typeKey ? translate(typeKey) : "";
  const rawContent = String(repliedMessage.message_content || "").trim();
  const snippet =
    type === "text" || type === "link"
      ? rawContent
      : caption ||
        typeLabel ||
        (looksLikeMediaPathOrUrl(rawContent) ? typeLabel : rawContent);
  const showThumb = PREVIEW_TYPES.has(type);
  const thumbUrl = showThumb ? replyThumbUrl(repliedMessage) : "";
  const blur = repliedMessage.blur_hash || repliedMessage.blurHash || null;

  return (
    <div
      onClick={() => {
        dispatch(
          updateNavigateToSpesificMessage({
            chat_id: repliedMessage?.chat_id,
            navigate_to_message: true,
            message_id: repliedMessage?.message_id,
          }),
        );
      }}
      className={
        isCompose
          ? "flex min-w-0 cursor-pointer items-stretch gap-2.5 px-3 py-2.5 text-darkText"
          : "my-1 flex w-full min-w-[13.5rem] max-w-full cursor-pointer items-stretch overflow-hidden rounded-lg bg-black/[0.06] dark:bg-white/[0.08] sm:min-w-[16rem]"
      }
    >
      {!isCompose ? (
        <div
          className="my-1.5 ml-1.5 w-[3px] shrink-0 self-stretch rounded-full bg-themeColor"
          aria-hidden
        />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col justify-center py-1.5 pr-1">
        <div
          className={
            isCompose
              ? "truncate text-[13px] font-medium text-themeColor dark:text-white"
              : "truncate px-2 text-[13px] font-medium text-themeColor dark:text-white"
          }
        >
          {author}
        </div>
        <div
          className={
            isCompose
              ? "line-clamp-1 text-[13px] leading-snug text-black/55 dark:text-white/70"
              : "line-clamp-1 px-2 text-[13px] leading-snug text-black/55 dark:text-white/70"
          }
        >
          {snippet}
        </div>
      </div>

      {showThumb ? (
        <div className="flex shrink-0 items-center self-stretch py-1.5 pr-1.5">
          {thumbUrl || blur ? (
            <ChatListMediaThumb
              messageType={type === "circle" ? "video" : type}
              thumbUrl={thumbUrl}
              blurHash={blur}
              sizeClassName="h-10 w-10"
              className="rounded-md"
            />
          ) : (
            <span
              className="inline-block h-10 w-10 shrink-0 rounded-md bg-[#3a3a3a]"
              aria-hidden
            />
          )}
        </div>
      ) : null}
    </div>
  );
}
